import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Search, 
  Eye,
  CheckCircle,
  Clock,
  Package,
  XCircle,
  Truck,
  Loader2,
  AlertTriangle,
  MapPin,
  Phone,
  User,
  Calendar,
  FileText,
  DollarSign,
  Filter,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  History,
  Map,
  Trash2,
  Activity,
  Edit,
  Save,
  Image,
  Plus,
  Upload,
  Mail,
  Send,
  Paperclip,
  MessageSquare,
  Download,
  ClipboardList
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SendSMSModal from './SendSMSModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Internal status colors (exact hex from requirements)
const INTERNAL_STATUS_COLORS = {
  new_booking: '#F97316',      // Bright orange - attention needed
  booking_reviewed: '#FBBF24', // Amber/yellow - reviewed but needs action
  confirmed: '#3B82F6',
  driver_assigned: '#6366F1',
  on_site: '#F59E0B',
  collection_in_progress: '#FB923C',
  collected: '#22C55E',
  internal_disposal_pending: '#A855F7',
  disposed: '#14B8A6',
  completed: '#15803D',
  on_hold: '#9CA3AF',
  partial_collection: '#F97316',
  failed_collection: '#EF4444',
  cancelled: '#6B7280',
};

// External status colors
const EXTERNAL_STATUS_COLORS = {
  booking_received: '#E5E7EB',
  scheduled: '#3B82F6',
  on_the_way: '#F59E0B',
  collected: '#22C55E',
  completed: '#15803D',
  cancelled: '#6B7280',
};

// Finance colors
const FINANCE_COLORS = {
  invoiced: '#FACC15',
  paid: '#16A34A',
};

const ATTENTION_STATUSES = ['on_hold', 'failed_collection', 'partial_collection'];

// Custom fetch wrapper using blob to avoid body stream issues
// Custom fetch using XMLHttpRequest to bypass Emergent's fetch interceptor
// Uses synchronous approach for more reliable request handling
const safeFetch = (url, options = {}) => {
  return new Promise((resolve) => {
    try {
      const xhr = new XMLHttpRequest();
      // Ensure URL is HTTPS
      const secureUrl = url.replace(/^http:/i, 'https:');
      xhr.open(options.method || 'GET', secureUrl, true);
      
      // Set headers
      if (options.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }
      
      xhr.onload = () => {
        let responseData = null;
        try {
          responseData = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        } catch {
          responseData = { detail: xhr.responseText || 'Invalid JSON response' };
        }
        
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          statusText: xhr.statusText,
          data: responseData
        });
      };
      
      xhr.onerror = () => {
        // Fallback: Try native fetch as backup
        console.log('XHR failed, trying native fetch as backup...');
        nativeFetchFallback(secureUrl, options).then(resolve);
      };
      
      xhr.ontimeout = () => {
        resolve({
          ok: false,
          status: 0,
          statusText: 'Timeout',
          data: { detail: 'Request timed out - please try again' }
        });
      };
      
      // Set timeout (30 seconds)
      xhr.timeout = 30000;
      
      // Send request
      xhr.send(options.body || null);
    } catch (err) {
      // If XHR fails completely, try native fetch
      console.log('XHR error, trying native fetch:', err);
      nativeFetchFallback(url, options).then(resolve);
    }
  });
};

// Native fetch fallback with blob-based response reading
const nativeFetchFallback = async (url, options = {}) => {
  try {
    const response = await window.fetch(url, options);
    let responseData = null;
    
    try {
      // Use blob first to avoid stream consumption issues
      const blob = await response.blob();
      const text = await blob.text();
      responseData = text ? JSON.parse(text) : {};
    } catch {
      responseData = { detail: 'Failed to parse response' };
    }
    
    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      data: responseData
    };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      statusText: 'Network Error',
      data: { detail: err.message || 'Network error - please try again' }
    };
  }
};

const AdminBookings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [statusOptions, setStatusOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [internalStatusFilter, setInternalStatusFilter] = useState([]);
  const [externalStatusFilter, setExternalStatusFilter] = useState([]);
  const [attentionNeeded, setAttentionNeeded] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [activityHistory, setActivityHistory] = useState([]);
  const [emailActivity, setEmailActivity] = useState([]);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Completed jobs report
  const [showReport, setShowReport] = useState(false);
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const pageSizeOptions = [10, 25, 50, 100];
  
  // Client filter state
  const [clientFilter, setClientFilter] = useState('');
  const [allClients, setAllClients] = useState([]);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [editNote, setEditNote] = useState('');
  
  // Photo states
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [isAddingPhoto, setIsAddingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  // Collection confirmation email states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: '',
    cc: 'hello@transa.com.au',
    bcc: '',
    subject: '',
    body: '',
    proofPhotoUrl: ''
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isUploadingProofPhoto, setIsUploadingProofPhoto] = useState(false);
  const [attachmentSizeBytes, setAttachmentSizeBytes] = useState(0);
  const ATTACHMENT_MAX_BYTES = 2 * 1024 * 1024; // Pabbly limit
  const [attachmentStatus, setAttachmentStatus] = useState(null);
  const [attachmentStatusLoading, setAttachmentStatusLoading] = useState(false);

  // Add Booking Modal states
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [subscriptionClients, setSubscriptionClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  const [addBookingForm, setAddBookingForm] = useState({
    clientType: 'subscription', // 'subscription' or 'one_time'
    clientId: '',
    clientName: '',
    clientMobile: '',
    clientEmail: '',
    clientBusinessName: '',
    siteAddress: '',
    customerName: '',
    customerMobile: '',
    siteNotes: '',
    hasPallets: false,
    palletCount: ''
  });
  const [addBookingError, setAddBookingError] = useState('');
  
  // Address autocomplete for add booking
  const [addBookingAddressSuggestions, setAddBookingAddressSuggestions] = useState([]);
  const [showAddBookingAddressSuggestions, setShowAddBookingAddressSuggestions] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddressesState] = useState(false);
  const addressDebounceRef = React.useRef(null);
  const sessionTokenRef = React.useRef(null);

  // Fetch status options on mount
  useEffect(() => {
    fetchStatusOptions();
    // Pre-load subscription clients so they're ready when modal opens
    fetchSubscriptionClients();
    // Fetch all clients for the filter dropdown
    fetchAllClients();
  }, []);

  // Auto-open a specific booking if bookingId is in the URL
  useEffect(() => {
    const bookingId = searchParams.get('bookingId');
    if (bookingId) {
      const openBooking = async () => {
        try {
          const token = localStorage.getItem('admin_token');
          const result = await safeFetch(`${API_URL}/api/v2/bookings/${bookingId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (result.ok && result.data) {
            setSelectedBooking(result.data);
            fetchStatusHistory(result.data.id);
            fetchActivityHistory(result.data.id);
            fetchEmailActivity(result.data.id);
            // Clean the URL param after opening
            searchParams.delete('bookingId');
            setSearchParams(searchParams, { replace: true });
          }
        } catch (err) {
          console.error('Failed to auto-open booking:', err);
        }
      };
      openBooking();
    }
  }, []);

  // Fetch bookings when filters or pagination change
  useEffect(() => {
    fetchBookings();
  }, [internalStatusFilter, externalStatusFilter, attentionNeeded, searchTerm, currentPage, pageSize, clientFilter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [internalStatusFilter, externalStatusFilter, attentionNeeded, searchTerm, clientFilter]);

  const fetchAllClients = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/subscription-clients/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok && Array.isArray(result.data)) {
        const formattedClients = result.data.map(c => ({
          id: c.id,
          name: c.business_name || c.name || 'Unknown',
          mobile: c.mobile,
          type: 'subscription'
        }));
        setAllClients(formattedClients);
      }
    } catch (err) {
      console.error('Failed to fetch clients for filter:', err);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const result = await safeFetch(`${API_URL}/api/v2/bookings/statuses`);
      if (result.ok) {
        setStatusOptions(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch status options:', err);
    }
  };

  // Fetch subscription clients for the add booking modal
  const fetchSubscriptionClients = async () => {
    try {
      setIsLoadingClients(true);
      const token = localStorage.getItem('admin_token');
      // Use safeFetch to bypass Emergent's fetch interceptor
      // Ensure URL uses HTTPS by replacing http: with https:
      // IMPORTANT: Use trailing slash to avoid 307 redirect with HTTP URL
      const baseUrl = (API_URL || '').replace(/^http:/i, 'https:');
      const url = `${baseUrl}/api/subscription-clients/?active_only=true`;
      console.log('Fetching subscription clients from:', url);
      const result = await safeFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) {
        setSubscriptionClients(result.data);
      } else {
        console.error('Failed to fetch subscription clients:', result.data);
      }
    } catch (err) {
      console.error('Failed to fetch subscription clients:', err);
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Address autocomplete for add booking modal
  const handleAddBookingAddressChange = async (value) => {
    setAddBookingForm(prev => ({ ...prev, siteAddress: value }));
    
    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }
    
    if (value.length < 3) {
      setAddBookingAddressSuggestions([]);
      setShowAddBookingAddressSuggestions(false);
      return;
    }
    
    addressDebounceRef.current = setTimeout(async () => {
      try {
        setIsLoadingAddressesState(true);
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = crypto.randomUUID();
        }
        // Use safeFetch to bypass Emergent's fetch interceptor
        // Ensure HTTPS and trailing slash to avoid 307 redirect issues
        const baseUrl = (API_URL || '').replace(/^http:/i, 'https:');
        const result = await safeFetch(
          `${baseUrl}/api/places/autocomplete/?input=${encodeURIComponent(value)}&session_token=${sessionTokenRef.current}`
        );
        if (result.ok) {
          setAddBookingAddressSuggestions(result.data.predictions || []);
          setShowAddBookingAddressSuggestions(true);
        }
      } catch (err) {
        console.error('Address autocomplete error:', err);
      } finally {
        setIsLoadingAddressesState(false);
      }
    }, 300);
  };

  const selectAddBookingAddress = (prediction) => {
    setAddBookingForm(prev => ({ ...prev, siteAddress: prediction.description }));
    setAddBookingAddressSuggestions([]);
    setShowAddBookingAddressSuggestions(false);
    sessionTokenRef.current = null;
  };

  // Handle add booking form submission
  const handleCreateBooking = async () => {
    setAddBookingError('');
    
    // Validation
    if (!addBookingForm.siteAddress) {
      setAddBookingError('Site address is required');
      return;
    }
    if (!addBookingForm.customerName || !addBookingForm.customerMobile) {
      setAddBookingError('Customer name and mobile are required');
      return;
    }
    if (addBookingForm.clientType === 'subscription' && !addBookingForm.clientId) {
      setAddBookingError('Please select a subscription client');
      return;
    }
    if (addBookingForm.clientType === 'one_time' && (!addBookingForm.clientName || !addBookingForm.clientMobile)) {
      setAddBookingError('Client name and mobile are required for one-time booking');
      return;
    }
    
    try {
      setIsCreatingBooking(true);
      const token = localStorage.getItem('admin_token');
      
      const payload = {
        client_type: addBookingForm.clientType,
        client_id: addBookingForm.clientType === 'subscription' ? addBookingForm.clientId : null,
        client_name: addBookingForm.clientName,
        client_mobile: addBookingForm.clientMobile,
        client_email: addBookingForm.clientEmail,
        client_business_name: addBookingForm.clientBusinessName,
        site_address: addBookingForm.siteAddress,
        customer_name: addBookingForm.customerName,
        customer_mobile: addBookingForm.customerMobile,
        site_notes: addBookingForm.siteNotes,
        has_pallets: addBookingForm.hasPallets,
        pallet_count: addBookingForm.hasPallets ? parseInt(addBookingForm.palletCount) || 0 : 0
      };
      
      // Use safeFetch to avoid body stream issues
      const result = await safeFetch(`${API_URL}/api/v2/bookings/admin-create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!result.ok) {
        throw new Error(result.data.detail || 'Failed to create booking');
      }
      
      const newBooking = result.data;
      
      // Reset form and close modal
      setShowAddBookingModal(false);
      setAddBookingForm({
        clientType: 'subscription',
        clientId: '',
        clientName: '',
        clientMobile: '',
        clientEmail: '',
        clientBusinessName: '',
        siteAddress: '',
        customerName: '',
        customerMobile: '',
        siteNotes: '',
        hasPallets: false,
        palletCount: ''
      });
      
      // Refresh bookings list
      fetchBookings();
      
      // Open the new booking in detail view
      setSelectedBooking(newBooking);
      
    } catch (err) {
      setAddBookingError(err.message);
    } finally {
      setIsCreatingBooking(false);
    }
  };

  // Open add booking modal
  const openAddBookingModal = () => {
    fetchSubscriptionClients();
    setShowAddBookingModal(true);
  };

  // Handle subscription client selection
  const handleClientSelect = (clientId) => {
    const client = subscriptionClients.find(c => c.id === clientId);
    if (client) {
      setAddBookingForm(prev => ({
        ...prev,
        clientId: clientId,
        clientName: client.name,
        clientMobile: client.mobile,
        clientEmail: client.email || '',
        clientBusinessName: client.business_name || ''
      }));
    }
  };

  const fetchBookings = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('admin_token');
      
      // Build query params
      const params = new URLSearchParams();
      if (internalStatusFilter.length > 0) {
        params.append('internal_status', internalStatusFilter.join(','));
      }
      if (externalStatusFilter.length > 0) {
        params.append('external_status', externalStatusFilter.join(','));
      }
      if (attentionNeeded) {
        params.append('attention_needed', 'true');
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (clientFilter) {
        params.append('client_id', clientFilter);
      }
      
      // Add pagination params
      params.append('page', currentPage.toString());
      params.append('page_size', pageSize.toString());
      
      const url = `${API_URL}/api/v2/bookings/?${params.toString()}`;
      
      const result = await safeFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!result.ok) throw new Error(result.data?.detail || 'Failed to fetch bookings');
      
      const data = result.data;
      
      // Handle paginated response
      if (data.bookings) {
        setBookings(data.bookings);
        setTotalBookings(data.total || 0);
      } else if (Array.isArray(data)) {
        // Fallback for non-paginated response
        setBookings(data);
        setTotalBookings(data.length);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [internalStatusFilter, externalStatusFilter, attentionNeeded, searchTerm, currentPage, pageSize, clientFilter]);

  const fetchStatusHistory = async (bookingId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/v2/bookings/${bookingId}/status-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) {
        setStatusHistory(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch status history:', err);
    }
  };

  const fetchActivityHistory = async (bookingId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/v2/bookings/${bookingId}/activity`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) {
        setActivityHistory(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch activity history:', err);
    }
  };

  const fetchEmailActivity = async (bookingId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/admin/email-log?booking_id=${bookingId}&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) {
        setEmailActivity(result.data.items || []);
      }
    } catch (err) {
      console.error('Failed to fetch email activity:', err);
    }
  };

  const deleteBooking = async (bookingId) => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/v2/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!result.ok) {
        throw new Error(result.data.detail || 'Failed to delete booking');
      }
      
      alert(result.data.message || 'Booking deleted successfully. Archive email sent to team.');
      setSelectedBooking(null);
      setShowDeleteConfirm(false);
      await fetchBookings();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const startEditMode = () => {
    // Initialize edit form with current booking data
    setEditForm({
      client_name: selectedBooking.client_name || '',
      client_business_name: selectedBooking.client_business_name || '',
      client_mobile: selectedBooking.client_mobile || '',
      client_email: selectedBooking.client_email || '',
      scheduled_date: selectedBooking.scheduled_date || '',
      scheduled_time_window: selectedBooking.scheduled_time_window || '',
      ops_notes: selectedBooking.ops_notes || '',
      total_price: selectedBooking.total_price || 0,
      waste_details: {
        customer_name: selectedBooking.waste_details?.customer_name || '',
        customer_mobile: selectedBooking.waste_details?.customer_mobile || '',
        site_address: selectedBooking.waste_details?.site_address || '',
        site_notes: selectedBooking.waste_details?.site_notes || '',
        pallets_present: selectedBooking.waste_details?.pallets_present || false,
        pallets_qty: selectedBooking.waste_details?.pallets_qty || 0,
      }
    });
    setEditNote('');
    setIsEditMode(true);
  };

  const cancelEditMode = () => {
    setIsEditMode(false);
    setEditForm({});
    setEditNote('');
  };

  const saveBookingEdit = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/v2/bookings/${selectedBooking.id}/edit`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...editForm,
          edit_note: editNote
        })
      });

      if (!result.ok) {
        throw new Error(result.data.detail || 'Failed to save changes');
      }
      
      const updated = result.data;
      
      // Show changes made
      if (updated.changes_made && updated.changes_made.length > 0) {
        const changesSummary = updated.changes_made.map(c => `• ${c.field}: "${c.from || '-'}" → "${c.to || '-'}"`).join('\n');
        alert(`Changes saved successfully!\n\n${changesSummary}`);
      } else {
        alert('No changes detected.');
      }
      
      setSelectedBooking(updated);
      setIsEditMode(false);
      await fetchBookings();
      await fetchActivityHistory(selectedBooking.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Helper to get full photo URL
  const getPhotoUrl = (photo) => {
    if (!photo) return '';
    // If photo is an object with url property
    if (typeof photo === 'object' && photo.url) {
      const url = photo.url;
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      if (url.startsWith('/api/')) {
        return `${API_URL}${url}`;
      }
      return url;
    }
    // If it's already a full URL, return as-is
    if (photo.startsWith('http://') || photo.startsWith('https://')) {
      return photo;
    }
    // If it's a relative API path, prepend the API URL
    if (photo.startsWith('/api/')) {
      return `${API_URL}${photo}`;
    }
    // Otherwise, return as-is
    return photo;
  };

  const getPhotoFilename = (photo) => {
    if (!photo) return '';
    // If photo is an object with filename property
    if (typeof photo === 'object' && photo.filename) {
      return photo.filename;
    }
    // Extract filename from URL
    if (typeof photo === 'string') {
      const parts = photo.split('/');
      return parts[parts.length - 1] || 'Photo';
    }
    return 'Photo';
  };

  const getPhotoViewLink = (photo) => {
    if (!photo) return null;
    if (typeof photo === 'object' && photo.view_link) {
      return photo.view_link;
    }
    return null;
  };

  const addPhoto = async () => {
    if (!newPhotoUrl.trim()) {
      alert('Please enter a photo URL');
      return;
    }
    
    setIsAddingPhoto(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/v2/bookings/${selectedBooking.id}/proof`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ proof_url: newPhotoUrl })
      });

      if (!result.ok) {
        throw new Error(result.data.detail || 'Failed to add photo');
      }
      
      setSelectedBooking(result.data);
      setNewPhotoUrl('');
      await fetchActivityHistory(selectedBooking.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsAddingPhoto(false);
    }
  };

  const removePhoto = async (photoUrl) => {
    if (!confirm('Are you sure you want to remove this photo?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/v2/bookings/${selectedBooking.id}/proof`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ proof_url: photoUrl })
      });

      if (!result.ok) {
        throw new Error(result.data.detail || 'Failed to remove photo');
      }
      
      setSelectedBooking(result.data);
      setSelectedPhoto(null);
      await fetchActivityHistory(selectedBooking.id);
    } catch (err) {
      alert(err.message);
    }
  };

  const updateInternalStatus = async (bookingId, newStatus) => {
    // Check if note is required
    const requiresNote = statusOptions?.statuses_requiring_note?.includes(newStatus);
    if (requiresNote && !statusNote.trim()) {
      alert(`A note is required when setting status to: ${statusOptions?.internal_statuses?.[newStatus]?.label || newStatus}`);
      return;
    }
    
    setIsUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/v2/bookings/${bookingId}/internal-status`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          internal_status: newStatus,
          note: statusNote || null
        })
      });

      if (!result.ok) {
        throw new Error(result.data.detail || 'Failed to update status');
      }
      
      setSelectedBooking(result.data);
      setStatusNote('');
      await fetchBookings();
      await fetchStatusHistory(bookingId);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateFinance = async (bookingId, field, value) => {
    setIsUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/v2/bookings/${bookingId}/finance`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });

      if (!result.ok) throw new Error(result.data.detail || 'Failed to update finance');
      
      setSelectedBooking(result.data);
      await fetchBookings();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  // Open email modal with pre-filled data
  const openCollectionEmailModal = () => {
    const booking = selectedBooking;
    const clientName = booking.client_name || 'Valued Client';
    const clientEmail = booking.client_email || '';
    const bookingRef = booking.booking_ref || '';
    const siteAddress = booking.waste_details?.site_address || 'the site';
    const isSubscription = booking.is_subscription_booking;
    
    // Get the most recent proof photo if available
    const proofPhotos = booking.proof_files || [];
    let latestProofUrl = '';
    if (proofPhotos.length > 0) {
      const lastPhoto = proofPhotos[proofPhotos.length - 1];
      // Handle both object format and string format
      if (typeof lastPhoto === 'object' && lastPhoto.url) {
        latestProofUrl = lastPhoto.url;
      } else if (typeof lastPhoto === 'string') {
        latestProofUrl = lastPhoto;
      }
    }
    
    // Build default email body
    let body = `Hi ${clientName},\n\n`;
    body += `We are pleased to confirm that the waste collection for your booking (${bookingRef}) has been successfully completed.\n\n`;
    body += `Collection Address: ${siteAddress}\n\n`;
    
    if (latestProofUrl) {
      body += `Please find attached a photo of the completed collection as proof of service.\n\n`;
    }
    
    body += `Thank you for choosing Transa for your waste removal needs. We truly appreciate your business and look forward to serving you again on your next order.\n\n`;
    body += `If you have any questions or need further assistance, please don't hesitate to contact us.\n\n`;
    body += `Warm regards,\nThe Transa Team\n`;
    body += `Phone: +61 407 502 587\n`;
    body += `Email: hello@transa.com.au`;
    
    setEmailForm({
      to: clientEmail,
      cc: 'hello@transa.com.au',
      bcc: '',
      subject: `Collection Completed - ${bookingRef}`,
      body: body,
      proofPhotoUrl: latestProofUrl
    });
    setAttachmentSizeBytes(0);
    // If there's a pre-selected proof URL, probe its size for the guard
    if (latestProofUrl) {
      const probeUrl = latestProofUrl.startsWith('/') ? `${API_URL}${latestProofUrl}` : latestProofUrl;
      fetch(probeUrl, { method: 'HEAD' })
        .then(r => setAttachmentSizeBytes(parseInt(r.headers.get('content-length') || '0', 10)))
        .catch(() => setAttachmentSizeBytes(0));
    }
    setShowEmailModal(true);
  };

  // Handle proof photo upload for email
  const handleProofPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }
    
    setIsUploadingProofPhoto(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('booking_id', selectedBooking.id);
      
      const result = await safeFetch(`${API_URL}/api/upload/photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!result.ok) {
        throw new Error(result.data?.detail || 'Failed to upload photo');
      }
      
      setEmailForm(prev => ({ ...prev, proofPhotoUrl: result.data.url }));
      setAttachmentSizeBytes(result.data.size || 0);
      
      // Also update the booking with this proof photo
      if (selectedBooking) {
        const bookingResult = await safeFetch(`${API_URL}/api/v2/bookings/${selectedBooking.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (bookingResult.ok) {
          setSelectedBooking(bookingResult.data);
        }
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUploadingProofPhoto(false);
    }
  };

  // Send collection confirmation email
  const sendCollectionEmail = async () => {
    if (!emailForm.to) {
      alert('Please enter a recipient email address');
      return;
    }
    
    setIsSendingEmail(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Parse CC and BCC into arrays
      const ccEmails = emailForm.cc ? emailForm.cc.split(',').map(e => e.trim()).filter(e => e) : [];
      const bccEmails = emailForm.bcc ? emailForm.bcc.split(',').map(e => e.trim()).filter(e => e) : [];
      
      const emailResult = await safeFetch(`${API_URL}/api/v2/bookings/${selectedBooking.id}/send-collection-confirmation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to_email: emailForm.to,
          cc_emails: ccEmails.length > 0 ? ccEmails : null,
          bcc_emails: bccEmails.length > 0 ? bccEmails : null,
          subject: emailForm.subject,
          body: emailForm.body,
          proof_photo_url: emailForm.proofPhotoUrl || null
        })
      });
      
      if (!emailResult.ok) {
        throw new Error(emailResult.data.detail || 'Failed to send email');
      }
      
      // Check if there was a warning about missing attachment
      if (emailResult.data.warning) {
        alert(`Email sent, but with a warning:\n\n${emailResult.data.warning}`);
      } else {
        alert('Collection confirmation email sent successfully!' + 
              (emailResult.data.has_attachment ? ' (with photo attached)' : ''));
      }
      setShowEmailModal(false);
      
      // Refresh booking data
      const bookingResult = await safeFetch(`${API_URL}/api/v2/bookings/${selectedBooking.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (bookingResult.ok) {
        setSelectedBooking(bookingResult.data);
      }
      await fetchActivityHistory(selectedBooking.id);
      await fetchEmailActivity(selectedBooking.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Australia/Melbourne'
    });
  };

  const getStatusBadge = (status, colors, options) => {
    const color = colors[status] || '#E5E7EB';
    const label = options?.[status]?.label || status?.replace(/_/g, ' ');
    const isLight = ['#E5E7EB', '#CBD5E1', '#FACC15'].includes(color);
    
    return (
      <span 
        className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
        style={{ 
          backgroundColor: color,
          color: isLight ? '#374151' : '#FFFFFF'
        }}
      >
        {label}
      </span>
    );
  };

  const toggleInternalFilter = (status) => {
    setInternalStatusFilter(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const toggleExternalFilter = (status) => {
    setExternalStatusFilter(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  // ---- Attachment status indicator ----
  const checkAttachmentStatus = useCallback(async (url) => {
    if (!url) { setAttachmentStatus(null); return; }
    setAttachmentStatusLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/admin/attachment-status?url=${encodeURIComponent(url)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAttachmentStatus(result.ok ? result.data : { available: false, detail: 'Could not verify attachment' });
    } catch {
      setAttachmentStatus({ available: false, detail: 'Could not verify attachment' });
    } finally {
      setAttachmentStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showEmailModal) {
      checkAttachmentStatus(emailForm.proofPhotoUrl);
    } else {
      setAttachmentStatus(null);
    }
  }, [emailForm.proofPhotoUrl, showEmailModal, checkAttachmentStatus]);

  // ---- Completed Jobs Report ----
  const toYmd = (d) => {
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };

  const openReport = () => {
    if (!reportFrom || !reportTo) {
      const today = new Date();
      const weekAgo = new Date();
      weekAgo.setDate(today.getDate() - 6);
      setReportFrom(toYmd(weekAgo));
      setReportTo(toYmd(today));
    }
    setReportError('');
    setShowReport(true);
  };

  const runCompletedReport = async () => {
    if (!reportFrom || !reportTo) {
      setReportError('Please select both a start and end date.');
      return;
    }
    setReportLoading(true);
    setReportError('');
    setReportData(null);
    try {
      const token = localStorage.getItem('admin_token');
      const url = `${API_URL}/api/admin/reports/completed-jobs?from_date=${reportFrom}&to_date=${reportTo}`;
      const result = await safeFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!result.ok) {
        const detail = (result.data && result.data.detail) || 'Failed to generate report';
        throw new Error(detail);
      }
      setReportData(result.data);
    } catch (err) {
      setReportError(err.message || 'Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const downloadReportCsv = () => {
    if (!reportData || !reportData.jobs || reportData.jobs.length === 0) return;
    const headers = ['Booking Ref', 'Installer / Client', 'Customer Name', 'Customer Mobile', 'Site Address', 'Service', 'Scheduled Date', 'Completed At', 'Driver'];
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const rows = reportData.jobs.map(j => [
      j.booking_ref, j.client_business_name, j.customer_name, j.customer_mobile,
      j.site_address, j.service_type, j.scheduled_date, j.completed_at, j.assigned_driver_name
    ].map(esc).join(','));
    const csv = [headers.map(esc).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `completed-jobs_${reportFrom}_to_${reportTo}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading && bookings.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0D9488]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Waste Collection Bookings</h1>
          <p className="text-[#64748B] mt-1">Manage rubbish collection jobs with two-layer status tracking</p>
        </div>
        <div className="flex gap-2">
          <Button
            className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
            onClick={openAddBookingModal}
            data-testid="add-booking-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Booking
          </Button>
          <Button
            variant="outline"
            className="border-[#E2E8F0]"
            onClick={openReport}
            data-testid="completed-report-button"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Completed Report
          </Button>
          <Button
            variant="outline"
            className="border-[#E2E8F0]"
            onClick={() => navigate('/admin/map-dispatch')}
          >
            <Map className="w-4 h-4 mr-2" />
            Map Dispatch
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E2E8F0]">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
              <Input
                type="text"
                placeholder="Search by phone, name, address, suburb, postcode, booking ref, email, notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-[#E2E8F0] text-[#111111] placeholder:text-[#94A3B8]"
              />
            </div>
            
            {/* Attention Needed Toggle */}
            <Button
              variant={attentionNeeded ? 'default' : 'outline'}
              className={attentionNeeded ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'border-[#E2E8F0]'}
              onClick={() => setAttentionNeeded(!attentionNeeded)}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Attention Needed
            </Button>
            
            {/* Filter Toggle */}
            <Button
              variant="outline"
              className="border-[#E2E8F0]"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {(internalStatusFilter.length > 0 || externalStatusFilter.length > 0) && (
                <span className="ml-2 px-2 py-0.5 bg-[#0D9488] text-white text-xs rounded-full">
                  {internalStatusFilter.length + externalStatusFilter.length}
                </span>
              )}
            </Button>
          </div>
          
          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-[#E2E8F0] grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Internal Status Filters */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-[#111111]">Internal Status</h3>
                  <div className="flex gap-2">
                    <button 
                      className="text-xs text-[#0D9488] hover:underline"
                      onClick={() => setInternalStatusFilter(Object.keys(statusOptions?.internal_statuses || {}))}
                    >
                      Select All
                    </button>
                    <button 
                      className="text-xs text-[#64748B] hover:underline"
                      onClick={() => setInternalStatusFilter([])}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {statusOptions?.internal_statuses && Object.entries(statusOptions.internal_statuses).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => toggleInternalFilter(key)}
                      className={`px-2 py-1 rounded text-xs font-medium border transition-all ${
                        internalStatusFilter.includes(key) 
                          ? 'ring-2 ring-offset-1 ring-[#0D9488]' 
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      style={{ 
                        backgroundColor: value.color,
                        color: ['#E5E7EB', '#CBD5E1'].includes(value.color) ? '#374151' : '#FFFFFF',
                        borderColor: value.color
                      }}
                    >
                      {value.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* External Status Filters */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-[#111111]">External Status (Client-Facing)</h3>
                  <div className="flex gap-2">
                    <button 
                      className="text-xs text-[#0D9488] hover:underline"
                      onClick={() => setExternalStatusFilter(Object.keys(statusOptions?.external_statuses || {}))}
                    >
                      Select All
                    </button>
                    <button 
                      className="text-xs text-[#64748B] hover:underline"
                      onClick={() => setExternalStatusFilter([])}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {statusOptions?.external_statuses && Object.entries(statusOptions.external_statuses).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => toggleExternalFilter(key)}
                      className={`px-2 py-1 rounded text-xs font-medium border transition-all ${
                        externalStatusFilter.includes(key) 
                          ? 'ring-2 ring-offset-1 ring-[#0D9488]' 
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      style={{ 
                        backgroundColor: value.color,
                        color: ['#E5E7EB'].includes(value.color) ? '#374151' : '#FFFFFF',
                        borderColor: value.color
                      }}
                    >
                      {value.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Client Filter */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-[#111111]">Filter by Client</h3>
                  {clientFilter && (
                    <button 
                      className="text-xs text-[#64748B] hover:underline"
                      onClick={() => setClientFilter('')}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <select
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  className="w-full max-w-md border border-[#E2E8F0] rounded-md px-3 py-2 text-sm bg-white text-[#111111]"
                >
                  <option value="">All Clients</option>
                  {allClients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} ({client.mobile})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <th className="px-3 py-3 text-center text-xs font-semibold text-[#64748B] uppercase tracking-wider w-12">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Booking</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Site Address</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Internal Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">External Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Finance</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center text-[#64748B]">
                    <Package className="w-12 h-12 mx-auto mb-3 text-[#E2E8F0]" />
                    <p>No bookings found</p>
                  </td>
                </tr>
              ) : (
                bookings.map((booking, index) => {
                  const isAttention = ATTENTION_STATUSES.includes(booking.internal_status);
                  const rowNumber = (currentPage - 1) * pageSize + index + 1;
                  
                  return (
                    <tr 
                      key={booking.id} 
                      className={`hover:bg-[#F8FAFC] transition-colors ${isAttention ? 'bg-orange-50' : ''}`}
                    >
                      <td className="px-3 py-4 text-center">
                        <span className="text-[#94A3B8] text-sm font-medium">{rowNumber}</span>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-[#111111] font-medium text-sm">{booking.booking_ref}</p>
                        <p className="text-[#94A3B8] text-xs">{booking.service_level_name}</p>
                        {booking.scheduled_date && (
                          <p className="text-[#64748B] text-xs mt-1 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {booking.scheduled_date}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-[#111111] text-sm">{booking.client_name}</p>
                        <p className="text-[#94A3B8] text-xs">{booking.client_mobile}</p>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-[#111111] text-sm truncate max-w-[200px]">
                          {booking.waste_details?.site_address || '-'}
                        </p>
                        {booking.needs_location_fix && (
                          <span className="text-orange-500 text-xs flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Needs location fix
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(booking.internal_status, INTERNAL_STATUS_COLORS, statusOptions?.internal_statuses)}
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(booking.external_status, EXTERNAL_STATUS_COLORS, statusOptions?.external_statuses)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          {booking.invoiced && (
                            <span 
                              className="px-2 py-0.5 rounded text-xs font-medium"
                              style={{ backgroundColor: FINANCE_COLORS.invoiced, color: '#374151' }}
                            >
                              Invoiced
                            </span>
                          )}
                          {booking.paid && (
                            <span 
                              className="px-2 py-0.5 rounded text-xs font-medium"
                              style={{ backgroundColor: FINANCE_COLORS.paid, color: '#FFFFFF' }}
                            >
                              Paid
                            </span>
                          )}
                          {!booking.invoiced && !booking.paid && (
                            <span className="text-[#94A3B8] text-xs">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-[#64748B] text-sm">{formatDate(booking.created_at)}</p>
                      </td>
                      <td className="px-4 py-4">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[#0D9488] hover:text-[#0D9488] hover:bg-[#F0FDFA]"
                          onClick={() => {
                            setSelectedBooking(booking);
                            fetchStatusHistory(booking.id);
                            fetchActivityHistory(booking.id);
                            fetchEmailActivity(booking.id);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        <div className="px-4 py-3 border-t border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC]">
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#64748B]">
              Showing {bookings.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} - {Math.min(currentPage * pageSize, totalBookings)} of {totalBookings} bookings
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#64748B]">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border border-[#E2E8F0] rounded-md px-2 py-1 text-sm bg-white text-[#111111]"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-2"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-1">
              {(() => {
                const totalPages = Math.ceil(totalBookings / pageSize);
                const pages = [];
                let startPage = Math.max(1, currentPage - 2);
                let endPage = Math.min(totalPages, startPage + 4);
                if (endPage - startPage < 4) {
                  startPage = Math.max(1, endPage - 4);
                }
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <Button
                      key={i}
                      variant={i === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 ${i === currentPage ? 'bg-[#0D9488] hover:bg-[#0B7A6E]' : ''}`}
                    >
                      {i}
                    </Button>
                  );
                }
                return pages;
              })()}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalBookings / pageSize), p + 1))}
              disabled={currentPage >= Math.ceil(totalBookings / pageSize)}
              className="px-2"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.ceil(totalBookings / pageSize))}
              disabled={currentPage >= Math.ceil(totalBookings / pageSize)}
              className="px-2"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Completed Jobs Report Modal */}
      {showReport && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" data-testid="completed-report-modal">
          <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-xl shadow-xl flex flex-col overflow-hidden">
            <div className="bg-white border-b border-[#E2E8F0] p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#111111] flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#0D9488]" />
                Collection Completed — Jobs Report
              </h2>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowReport(false)}
                data-testid="report-close-button"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-4 border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-[#475569] mb-1">From date</label>
                  <Input
                    type="date"
                    value={reportFrom}
                    onChange={(e) => setReportFrom(e.target.value)}
                    className="bg-white border-[#E2E8F0]"
                    data-testid="report-from-date"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-[#475569] mb-1">To date</label>
                  <Input
                    type="date"
                    value={reportTo}
                    onChange={(e) => setReportTo(e.target.value)}
                    className="bg-white border-[#E2E8F0]"
                    data-testid="report-to-date"
                  />
                </div>
                <Button
                  className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                  onClick={runCompletedReport}
                  disabled={reportLoading}
                  data-testid="report-run-button"
                >
                  {reportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Run Report
                </Button>
                <Button
                  variant="outline"
                  className="border-[#E2E8F0]"
                  onClick={downloadReportCsv}
                  disabled={!reportData || !reportData.jobs || reportData.jobs.length === 0}
                  data-testid="report-download-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </Button>
              </div>
              <p className="text-xs text-[#94A3B8] mt-2">
                Lists jobs marked as <strong>Completed</strong>, by completion date (Melbourne time). Both dates inclusive.
              </p>
              {reportError && (
                <p className="text-sm text-[#DC2626] mt-2" data-testid="report-error">{reportError}</p>
              )}
            </div>

            <div className="flex-1 overflow-auto p-4">
              {reportData && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-[#475569]" data-testid="report-total">
                      <strong>{reportData.total}</strong> completed {reportData.total === 1 ? 'job' : 'jobs'} between {reportData.from_date} and {reportData.to_date}
                    </p>
                  </div>
                  {reportData.jobs.length === 0 ? (
                    <div className="text-center py-12 text-[#94A3B8]">
                      <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      No completed jobs found in this date range.
                    </div>
                  ) : (
                    <div className="overflow-x-auto border border-[#E2E8F0] rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-[#F8FAFC] text-left text-[#475569]">
                            <th className="px-3 py-2 font-medium">Ref</th>
                            <th className="px-3 py-2 font-medium">Installer / Client</th>
                            <th className="px-3 py-2 font-medium">Customer</th>
                            <th className="px-3 py-2 font-medium">Mobile</th>
                            <th className="px-3 py-2 font-medium">Site Address</th>
                            <th className="px-3 py-2 font-medium">Completed At</th>
                            <th className="px-3 py-2 font-medium">Driver</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.jobs.map((j, idx) => (
                            <tr key={idx} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`report-row-${idx}`}>
                              <td className="px-3 py-2 font-medium text-[#0D9488] whitespace-nowrap">{j.booking_ref}</td>
                              <td className="px-3 py-2 text-[#111111]">{j.client_business_name || '—'}</td>
                              <td className="px-3 py-2 text-[#475569]">{j.customer_name || '—'}</td>
                              <td className="px-3 py-2 text-[#475569] whitespace-nowrap">{j.customer_mobile || '—'}</td>
                              <td className="px-3 py-2 text-[#475569]">{j.site_address || '—'}</td>
                              <td className="px-3 py-2 text-[#475569] whitespace-nowrap">{j.completed_at || '—'}</td>
                              <td className="px-3 py-2 text-[#475569]">{j.assigned_driver_name || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
              {!reportData && !reportLoading && (
                <div className="text-center py-12 text-[#94A3B8]">
                  Select a date range and click <strong>Run Report</strong>.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-start justify-end">
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl animate-in slide-in-from-right">
            <div className="sticky top-0 bg-white border-b border-[#E2E8F0] p-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-[#111111]">
                {isEditMode ? 'Edit Booking' : 'Booking Details'}
              </h2>
              <div className="flex items-center gap-2">
                {!isEditMode ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#0D9488] text-[#0D9488] hover:bg-[#F0FDFA]"
                    onClick={startEditMode}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelEditMode}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="bg-[#0D9488] hover:bg-[#0F766E]"
                      onClick={saveBookingEdit}
                      disabled={isSaving}
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
                    </Button>
                  </>
                )}
                <button 
                  onClick={() => {
                    setSelectedBooking(null);
                    setStatusHistory([]);
                    setActivityHistory([]);
                    setShowDeleteConfirm(false);
                    setIsEditMode(false);
                    setEditForm({});
                    setSelectedPhoto(null);
                  }}
                  className="text-[#94A3B8] hover:text-[#111111] ml-2"
                >
                <X className="w-6 h-6" />
              </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Booking ID and Status */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-lg font-mono font-bold text-[#111111]">{selectedBooking.booking_ref}</span>
                {getStatusBadge(selectedBooking.internal_status, INTERNAL_STATUS_COLORS, statusOptions?.internal_statuses)}
                {getStatusBadge(selectedBooking.external_status, EXTERNAL_STATUS_COLORS, statusOptions?.external_statuses)}
                {ATTENTION_STATUSES.includes(selectedBooking.internal_status) && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Attention
                  </span>
                )}
              </div>

              {/* Edit Note - shown in edit mode */}
              {isEditMode && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <label className="text-xs font-medium text-yellow-700 block mb-1">Edit Note (Optional)</label>
                  <Input
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    placeholder="Reason for changes..."
                    className="text-sm bg-white"
                  />
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Client Info */}
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                  <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Client
                  </p>
                  {isEditMode ? (
                    <div className="space-y-2">
                      <Input
                        value={editForm.client_name || ''}
                        onChange={(e) => setEditForm({...editForm, client_name: e.target.value})}
                        placeholder="Client Name"
                        className="text-sm"
                      />
                      <Input
                        value={editForm.client_mobile || ''}
                        onChange={(e) => setEditForm({...editForm, client_mobile: e.target.value})}
                        placeholder="Client Mobile"
                        className="text-sm"
                      />
                      <Input
                        value={editForm.client_business_name || ''}
                        onChange={(e) => setEditForm({...editForm, client_business_name: e.target.value})}
                        placeholder="Business Name"
                        className="text-sm"
                      />
                      <Input
                        value={editForm.client_email || ''}
                        onChange={(e) => setEditForm({...editForm, client_email: e.target.value})}
                        placeholder="Client Email"
                        className="text-sm"
                      />
                    </div>
                  ) : (
                    <>
                      <p className="text-[#111111] font-medium">{selectedBooking.client_name}</p>
                      <p className="text-[#64748B] text-sm">{selectedBooking.client_mobile}</p>
                      {selectedBooking.client_business_name && (
                        <p className="text-[#94A3B8] text-xs">{selectedBooking.client_business_name}</p>
                      )}
                      {selectedBooking.client_email && (
                        <p className="text-[#94A3B8] text-xs">{selectedBooking.client_email}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Customer/Site Contact */}
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                  <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Customer (Site Contact)
                  </p>
                  {isEditMode ? (
                    <div className="space-y-2">
                      <Input
                        value={editForm.waste_details?.customer_name || ''}
                        onChange={(e) => setEditForm({
                          ...editForm, 
                          waste_details: {...editForm.waste_details, customer_name: e.target.value}
                        })}
                        placeholder="Customer Name"
                        className="text-sm"
                      />
                      <Input
                        value={editForm.waste_details?.customer_mobile || ''}
                        onChange={(e) => setEditForm({
                          ...editForm, 
                          waste_details: {...editForm.waste_details, customer_mobile: e.target.value}
                        })}
                        placeholder="Customer Mobile"
                        className="text-sm"
                      />
                    </div>
                  ) : (
                    <>
                      <p className="text-[#111111] font-medium">{selectedBooking.waste_details?.customer_name}</p>
                      {selectedBooking.waste_details?.customer_mobile ? (
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-[#64748B] text-sm flex-1">{selectedBooking.waste_details.customer_mobile}</p>
                          <a
                            href={`tel:${selectedBooking.waste_details.customer_mobile}`}
                            className="p-1.5 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                            title="Call customer"
                            data-testid="bookings-call-customer-btn"
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </a>
                          <button
                            type="button"
                            onClick={() => setSmsModalOpen(true)}
                            className="p-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            title="Send SMS"
                            data-testid="bookings-sms-customer-btn"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <p className="text-[#64748B] text-sm">No mobile</p>
                      )}
                    </>
                  )}
                </div>

                {/* Site Address */}
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0] md:col-span-2">
                  <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Site Address
                  </p>
                  {isEditMode ? (
                    <div className="space-y-2">
                      <Input
                        value={editForm.waste_details?.site_address || ''}
                        onChange={(e) => setEditForm({
                          ...editForm, 
                          waste_details: {...editForm.waste_details, site_address: e.target.value}
                        })}
                        placeholder="Site Address"
                        className="text-sm"
                      />
                      <Input
                        value={editForm.waste_details?.site_notes || ''}
                        onChange={(e) => setEditForm({
                          ...editForm, 
                          waste_details: {...editForm.waste_details, site_notes: e.target.value}
                        })}
                        placeholder="Site Notes"
                        className="text-sm"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editForm.waste_details?.pallets_present || false}
                          onChange={(e) => setEditForm({
                            ...editForm, 
                            waste_details: {...editForm.waste_details, pallets_present: e.target.checked}
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Pallets Present</span>
                        {editForm.waste_details?.pallets_present && (
                          <Input
                            type="number"
                            value={editForm.waste_details?.pallets_qty || 0}
                            onChange={(e) => setEditForm({
                              ...editForm, 
                              waste_details: {...editForm.waste_details, pallets_qty: parseInt(e.target.value) || 0}
                            })}
                            placeholder="Qty"
                            className="text-sm w-20"
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-[#111111]">{selectedBooking.waste_details?.site_address}</p>
                      {selectedBooking.waste_details?.site_notes && (
                        <p className="text-[#94A3B8] text-sm mt-1">Notes: {selectedBooking.waste_details.site_notes}</p>
                      )}
                      {selectedBooking.waste_details?.pallets_present && (
                        <p className="text-[#64748B] text-sm mt-1">Pallets: {selectedBooking.waste_details.pallets_qty || 'Yes'}</p>
                      )}
                      {selectedBooking.needs_location_fix && (
                        <p className="text-orange-500 text-sm mt-2 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" /> Location needs manual fix for map display
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Schedule */}
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                  <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Schedule
                  </p>
                  {isEditMode ? (
                    <div className="space-y-2">
                      <Input
                        type="date"
                        value={editForm.scheduled_date || ''}
                        onChange={(e) => setEditForm({...editForm, scheduled_date: e.target.value})}
                        className="text-sm"
                      />
                      <select
                        value={editForm.scheduled_time_window || ''}
                        onChange={(e) => setEditForm({...editForm, scheduled_time_window: e.target.value})}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                      >
                        <option value="">Select time window</option>
                        <option value="7:00 AM - 10:00 AM">7:00 AM - 10:00 AM</option>
                        <option value="10:00 AM - 1:00 PM">10:00 AM - 1:00 PM</option>
                        <option value="1:00 PM - 4:00 PM">1:00 PM - 4:00 PM</option>
                        <option value="4:00 PM - 7:00 PM">4:00 PM - 7:00 PM</option>
                      </select>
                    </div>
                  ) : (
                    <>
                      <p className="text-[#111111]">
                        {selectedBooking.scheduled_date || 'Not scheduled'}
                      </p>
                      {selectedBooking.scheduled_time_window && (
                        <p className="text-[#64748B] text-sm">{selectedBooking.scheduled_time_window}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Price */}
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                  <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> Price
                  </p>
                  {isEditMode ? (
                    <div className="flex items-center gap-1">
                      <span className="text-[#64748B]">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={editForm.total_price || 0}
                        onChange={(e) => setEditForm({...editForm, total_price: parseFloat(e.target.value) || 0})}
                        className="text-sm"
                      />
                    </div>
                  ) : (
                    <>
                      <p className="text-[#111111] font-medium">${selectedBooking.total_price?.toFixed(2) || '0.00'}</p>
                      <p className="text-[#64748B] text-sm">{selectedBooking.service_level_name}</p>
                    </>
                  )}
                </div>

                {/* Operations Notes */}
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0] md:col-span-2">
                  <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Operations Notes
                  </p>
                  {isEditMode ? (
                    <textarea
                      value={editForm.ops_notes || ''}
                      onChange={(e) => setEditForm({...editForm, ops_notes: e.target.value})}
                      placeholder="Internal notes for operations team..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md min-h-[80px]"
                    />
                  ) : (
                    <p className="text-[#111111]">{selectedBooking.ops_notes || 'No notes'}</p>
                  )}
                </div>
              </div>

              {/* Client Site Photos Section */}
              {selectedBooking.site_photos && selectedBooking.site_photos.length > 0 && (
                <div className="border-t border-[#E2E8F0] pt-6">
                  <p className="text-[#111111] font-medium mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" /> Client Site Photos ({selectedBooking.site_photos.length})
                    <span className="text-xs text-[#94A3B8] font-normal ml-1">(uploaded by client)</span>
                  </p>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    {selectedBooking.site_photos.map((photo, idx) => {
                      const filename = getPhotoFilename(photo);
                      const viewLink = getPhotoViewLink(photo);
                      const isGoogleDrive = typeof photo === 'object' && photo.storage === 'google_drive';
                      
                      return (
                        <div key={idx} className="group">
                          <div 
                            className="relative cursor-pointer aspect-square bg-gray-100 rounded-lg overflow-hidden border border-blue-200"
                            onClick={() => setSelectedPhoto(photo)}
                          >
                            <img 
                              src={getPhotoUrl(photo)} 
                              alt={filename}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">No Image</text></svg>';
                              }}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            {isGoogleDrive && (
                              <div className="absolute bottom-1 left-1 p-1 bg-white/90 rounded text-xs flex items-center gap-1">
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 2L4.5 14H12L7.5 22L19.5 10H12L16.5 2H12Z" fill="#4285F4"/>
                                </svg>
                              </div>
                            )}
                            <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-blue-500 text-white text-xs rounded">
                              Client
                            </div>
                          </div>
                          {/* Filename display */}
                          <div className="mt-1.5 px-0.5">
                            {viewLink ? (
                              <a 
                                href={viewLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-[#0D9488] hover:underline truncate block"
                                title={filename}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {filename}
                              </a>
                            ) : (
                              <p className="text-xs text-[#64748B] truncate" title={filename}>
                                {filename}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Admin Photos Section (Proof of Collection) */}
              <div className="border-t border-[#E2E8F0] pt-6">
                <p className="text-[#111111] font-medium mb-3 flex items-center gap-2">
                  <Image className="w-4 h-4" /> Proof Photos ({selectedBooking.proof_files?.length || 0})
                  <span className="text-xs text-[#94A3B8] font-normal ml-1">(admin uploads)</span>
                </p>
                
                {/* Photo Grid */}
                {selectedBooking.proof_files && selectedBooking.proof_files.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    {selectedBooking.proof_files.map((photo, idx) => {
                      const filename = getPhotoFilename(photo);
                      const viewLink = getPhotoViewLink(photo);
                      const isGoogleDrive = typeof photo === 'object' && photo.storage === 'google_drive';
                      
                      return (
                        <div key={idx} className="group">
                          <div 
                            className="relative cursor-pointer aspect-square bg-gray-100 rounded-lg overflow-hidden border border-[#E2E8F0]"
                            onClick={() => setSelectedPhoto(photo)}
                          >
                            <img 
                              src={getPhotoUrl(photo)} 
                              alt={filename}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">No Image</text></svg>';
                              }}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removePhoto(photo);
                              }}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            {isGoogleDrive && (
                              <div className="absolute bottom-1 left-1 p-1 bg-white/90 rounded text-xs flex items-center gap-1">
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 2L4.5 14H12L7.5 22L19.5 10H12L16.5 2H12Z" fill="#4285F4"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          {/* Filename display */}
                          <div className="mt-1.5 px-0.5">
                            {viewLink ? (
                              <a 
                                href={viewLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-[#0D9488] hover:underline truncate block"
                                title={filename}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {filename}
                              </a>
                            ) : (
                              <p className="text-xs text-[#64748B] truncate" title={filename}>
                                {filename}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[#94A3B8] text-sm mb-4">No photos uploaded</p>
                )}
                
                {/* Add Photo - File Upload */}
                <div className="space-y-3">
                  {/* File Upload */}
                  <div className="flex items-center gap-2">
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          // Check file size (10MB limit)
                          if (file.size > 10 * 1024 * 1024) {
                            alert('File too large. Maximum size: 10MB');
                            return;
                          }
                          
                          setIsAddingPhoto(true);
                          try {
                            const token = localStorage.getItem('admin_token');
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('booking_id', selectedBooking.id);
                            
                            const result = await safeFetch(`${API_URL}/api/upload/photo`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` },
                              body: formData
                            });
                            
                            if (!result.ok) {
                              throw new Error(result.data?.detail || 'Failed to upload photo');
                            }
                            
                            // Refresh booking to show new photo
                            const bookingResult = await safeFetch(`${API_URL}/api/v2/bookings/${selectedBooking.id}`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (bookingResult.ok) {
                              setSelectedBooking(bookingResult.data);
                            }
                            
                            await fetchActivityHistory(selectedBooking.id);
                          } catch (err) {
                            alert(err.message);
                          } finally {
                            setIsAddingPhoto(false);
                            e.target.value = ''; // Reset file input
                          }
                        }}
                      />
                      <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#E2E8F0] rounded-lg hover:border-[#0D9488] hover:bg-[#F0FDFA] transition-colors">
                        {isAddingPhoto ? (
                          <Loader2 className="w-5 h-5 animate-spin text-[#0D9488]" />
                        ) : (
                          <Upload className="w-5 h-5 text-[#64748B]" />
                        )}
                        <span className="text-sm text-[#64748B]">
                          {isAddingPhoto ? 'Uploading...' : 'Click to upload photo'}
                        </span>
                      </div>
                    </label>
                  </div>
                  
                  {/* OR Divider */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-[#E2E8F0]"></div>
                    <span className="text-xs text-[#94A3B8]">OR</span>
                    <div className="flex-1 h-px bg-[#E2E8F0]"></div>
                  </div>
                  
                  {/* URL Input */}
                  <div className="flex gap-2">
                    <Input
                      value={newPhotoUrl}
                      onChange={(e) => setNewPhotoUrl(e.target.value)}
                      placeholder="Paste photo URL..."
                      className="text-sm flex-1"
                    />
                    <Button
                      size="sm"
                      onClick={addPhoto}
                      disabled={isAddingPhoto || !newPhotoUrl.trim()}
                      className="bg-[#0D9488] hover:bg-[#0F766E]"
                    >
                      {isAddingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Photo Modal */}
              {selectedPhoto && (
                <div 
                  className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4"
                  onClick={() => setSelectedPhoto(null)}
                >
                  <div className="relative max-w-4xl max-h-[90vh] flex flex-col">
                    <img 
                      src={getPhotoUrl(selectedPhoto)} 
                      alt="Full size"
                      className="max-w-full max-h-[80vh] object-contain rounded-lg"
                    />
                    {/* File info bar */}
                    <div className="mt-3 bg-white/95 rounded-lg p-3 flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#111111] truncate">
                          {getPhotoFilename(selectedPhoto)}
                        </p>
                        {typeof selectedPhoto === 'object' && selectedPhoto.storage === 'google_drive' && (
                          <p className="text-xs text-[#64748B] flex items-center gap-1 mt-0.5">
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
                              <path d="M12 2L4.5 14H12L7.5 22L19.5 10H12L16.5 2H12Z" fill="#4285F4"/>
                            </svg>
                            Stored in Google Drive
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {getPhotoViewLink(selectedPhoto) && (
                          <a
                            href={getPhotoViewLink(selectedPhoto)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="px-3 py-2 bg-[#0D9488] text-white rounded-lg text-sm flex items-center gap-2 hover:bg-[#0F766E] transition-colors"
                          >
                            <Eye className="w-4 h-4" /> Open in Drive
                          </a>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removePhoto(selectedPhoto);
                          }}
                          className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm flex items-center gap-2 hover:bg-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" /> Remove
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedPhoto(null)}
                      className="absolute -top-3 -right-3 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Finance Flags */}
              <div className="border-t border-[#E2E8F0] pt-6">
                <p className="text-[#111111] font-medium mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Finance
                </p>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedBooking.invoiced || false}
                      onChange={(e) => updateFinance(selectedBooking.id, 'invoiced', e.target.checked)}
                      disabled={isUpdating}
                      className="w-4 h-4 rounded border-gray-300 text-[#FACC15] focus:ring-[#FACC15]"
                    />
                    <span className="text-sm">Invoiced</span>
                    {selectedBooking.invoiced_date && (
                      <span className="text-xs text-[#94A3B8]">({formatDate(selectedBooking.invoiced_date)})</span>
                    )}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedBooking.paid || false}
                      onChange={(e) => updateFinance(selectedBooking.id, 'paid', e.target.checked)}
                      disabled={isUpdating}
                      className="w-4 h-4 rounded border-gray-300 text-[#16A34A] focus:ring-[#16A34A]"
                    />
                    <span className="text-sm">Paid</span>
                    {selectedBooking.paid_date && (
                      <span className="text-xs text-[#94A3B8]">({formatDate(selectedBooking.paid_date)})</span>
                    )}
                  </label>
                </div>
              </div>

              {/* Internal Status Update */}
              <div className="border-t border-[#E2E8F0] pt-6">
                <p className="text-[#111111] font-medium mb-3">Update Internal Status</p>
                
                {/* Note input for statuses that require it */}
                <div className="mb-3">
                  <Input
                    placeholder="Add a note (required for On Hold, Failed, Partial, Cancelled)"
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    className="text-sm"
                  />
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {statusOptions?.internal_statuses && Object.entries(statusOptions.internal_statuses).map(([key, value]) => {
                    const isCurrentStatus = selectedBooking.internal_status === key;
                    const isLight = ['#E5E7EB', '#CBD5E1'].includes(value.color);
                    
                    return (
                      <Button
                        key={key}
                        size="sm"
                        onClick={() => updateInternalStatus(selectedBooking.id, key)}
                        disabled={isUpdating || isCurrentStatus}
                        className={`text-xs ${isCurrentStatus ? 'ring-2 ring-offset-1 ring-[#0D9488]' : ''}`}
                        style={{ 
                          backgroundColor: value.color,
                          color: isLight ? '#374151' : '#FFFFFF',
                          opacity: isCurrentStatus ? 1 : 0.8
                        }}
                      >
                        {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : value.label}
                      </Button>
                    );
                  })}
                </div>
                
                <p className="text-[#94A3B8] text-xs mt-2">
                  External status will auto-update: {statusOptions?.external_statuses?.[selectedBooking.external_status]?.label}
                </p>
              </div>

              {/* Status History */}
              <div className="border-t border-[#E2E8F0] pt-6">
                <p className="text-[#111111] font-medium mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" /> Status History
                </p>
                {statusHistory.length === 0 ? (
                  <p className="text-[#94A3B8] text-sm">No status changes recorded</p>
                ) : (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {statusHistory.map((entry, idx) => (
                      <div key={entry.id || idx} className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E2E8F0]">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-[#64748B]">{formatDate(entry.timestamp)}</span>
                          <span className="text-xs text-[#94A3B8]">by {entry.changed_by}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(entry.from_status, INTERNAL_STATUS_COLORS, statusOptions?.internal_statuses)}
                          <span className="text-[#94A3B8]">→</span>
                          {getStatusBadge(entry.to_status, INTERNAL_STATUS_COLORS, statusOptions?.internal_statuses)}
                        </div>
                        {entry.note && (
                          <p className="text-[#64748B] text-sm mt-2 bg-white p-2 rounded border border-[#E2E8F0]">
                            {entry.note}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Activity History */}
              <div className="border-t border-[#E2E8F0] pt-6">
                <p className="text-[#111111] font-medium mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4" /> Activity History
                </p>
                {activityHistory.length === 0 ? (
                  <p className="text-[#94A3B8] text-sm">No activity recorded</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {activityHistory.map((entry, idx) => (
                      <div key={entry.id || idx} className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E2E8F0]">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-[#111111]">
                            {entry.event_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                          <span className="text-xs text-[#94A3B8]">{formatDate(entry.timestamp)}</span>
                        </div>
                        <p className="text-sm text-[#64748B]">{entry.description}</p>
                        {entry.user_email && (
                          <p className="text-xs text-[#94A3B8] mt-1">by {entry.user_email}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Email Activity */}
              <div className="border-t border-[#E2E8F0] pt-6" data-testid="email-activity-section">
                <p className="text-[#111111] font-medium mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email Activity
                  {emailActivity.length > 0 && (
                    <span className="text-xs bg-[#F1F5F9] text-[#64748B] rounded-full px-2 py-0.5">
                      {emailActivity.length}
                    </span>
                  )}
                </p>
                {emailActivity.length === 0 ? (
                  <p className="text-[#94A3B8] text-sm">No emails sent for this booking</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {emailActivity.map((entry) => (
                      <div
                        key={entry.id}
                        className={`rounded-lg p-3 border ${
                          entry.success
                            ? 'bg-[#F8FAFC] border-[#E2E8F0]'
                            : 'bg-red-50 border-red-200'
                        }`}
                        data-testid={`email-log-${entry.id}`}
                      >
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <span className="text-xs font-medium text-[#111111] flex items-center gap-1.5">
                            {entry.success ? (
                              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" title="Sent successfully" />
                            ) : (
                              <span className="inline-block w-2 h-2 rounded-full bg-red-500" title="Failed" />
                            )}
                            {entry.email_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            <span className="text-[10px] font-normal text-[#94A3B8] uppercase tracking-wide ml-1">
                              via {entry.route}
                            </span>
                          </span>
                          <span className="text-xs text-[#94A3B8] whitespace-nowrap">
                            {formatDate(entry.sent_at)}
                          </span>
                        </div>
                        <p className="text-sm text-[#111111] font-medium leading-snug">{entry.subject || '(no subject)'}</p>
                        <p className="text-xs text-[#64748B] mt-0.5">
                          To: {entry.to}
                          {entry.cc && <span className="ml-2">CC: {entry.cc}</span>}
                        </p>
                        {entry.has_attachment && (
                          <p className="text-[11px] text-[#64748B] mt-0.5 flex items-center gap-1">
                            📎 {entry.attachment_name || 'attachment'}
                          </p>
                        )}
                        {!entry.success && entry.error && (
                          <p className="text-xs text-red-700 mt-1 italic">⚠️ {entry.error}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Send Collection Confirmation - Show when status is collected or beyond */}
              {['collected', 'internal_disposal_pending', 'disposed', 'completed'].includes(selectedBooking.internal_status) && (
                <div className="border-t border-[#E2E8F0] pt-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-medium mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Collection Confirmation
                    </p>
                    <p className="text-green-700 text-sm mb-3">
                      Send an email to notify the client that their waste has been collected.
                      {selectedBooking.collection_confirmation_sent && (
                        <span className="block text-green-600 text-xs mt-1">
                          ✓ Already sent on {formatDate(selectedBooking.collection_confirmation_sent_at)}
                        </span>
                      )}
                    </p>
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      onClick={openCollectionEmailModal}
                      data-testid="send-collection-confirmation-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {selectedBooking.collection_confirmation_sent ? 'Send Another Confirmation' : 'Send Collection Confirmation'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Delete Booking */}
              <div className="border-t border-[#E2E8F0] pt-6">
                {!showDeleteConfirm ? (
                  <Button
                    variant="outline"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Booking
                  </Button>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-medium mb-2">Confirm Deletion</p>
                    <p className="text-red-600 text-sm mb-4">
                      This will permanently delete booking <strong>{selectedBooking.booking_ref}</strong>.
                      An archive email with all booking details will be sent to the team.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => deleteBooking(selectedBooking.id)}
                        disabled={isDeleting}
                      >
                        {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SMS Modal (native handoff) */}
      {smsModalOpen && selectedBooking && (
        <SendSMSModal
          booking={selectedBooking}
          defaultTo={selectedBooking.waste_details?.customer_mobile || ''}
          onClose={() => setSmsModalOpen(false)}
        />
      )}

      {/* Collection Confirmation Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#111111] flex items-center gap-2">
                <Mail className="w-5 h-5 text-green-600" />
                Send Collection Confirmation
              </h2>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-[#94A3B8] hover:text-[#111111]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* To Field */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  To <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, to: e.target.value }))}
                  placeholder="client@email.com"
                  className="w-full"
                  data-testid="email-to-input"
                />
              </div>
              
              {/* CC Field */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  CC <span className="text-[#94A3B8] text-xs">(comma separated)</span>
                </label>
                <Input
                  type="text"
                  value={emailForm.cc}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, cc: e.target.value }))}
                  placeholder="cc@email.com, another@email.com"
                  className="w-full"
                  data-testid="email-cc-input"
                />
              </div>
              
              {/* BCC Field */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  BCC <span className="text-[#94A3B8] text-xs">(comma separated)</span>
                </label>
                <Input
                  type="text"
                  value={emailForm.bcc}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, bcc: e.target.value }))}
                  placeholder="bcc@email.com"
                  className="w-full"
                  data-testid="email-bcc-input"
                />
              </div>
              
              {/* Subject Field */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  Subject
                </label>
                <Input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject"
                  className="w-full"
                  data-testid="email-subject-input"
                />
              </div>
              
              {/* Body Field */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">
                  Message
                </label>
                <textarea
                  value={emailForm.body}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Email message..."
                  rows={10}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent resize-y"
                  data-testid="email-body-input"
                />
              </div>
              
              {/* Proof Photo Attachment */}
              <div className="border border-[#E2E8F0] rounded-lg p-4 bg-[#F8FAFC]">
                <label className="block text-sm font-medium text-[#374151] mb-2 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Proof Photo Attachment <span className="text-[#94A3B8] text-xs">(optional)</span>
                </label>
                
                {/* Warning about file persistence */}
                <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                  <strong>Note:</strong> Previously uploaded photos may not be available after deployment. 
                  Please upload a new photo if the existing one doesn't load.
                </div>
                
                {emailForm.proofPhotoUrl ? (
                  <div className="flex items-center gap-3">
                    <img 
                      src={emailForm.proofPhotoUrl.startsWith('/') ? `${API_URL}${emailForm.proofPhotoUrl}` : emailForm.proofPhotoUrl}
                      alt="Proof" 
                      className="w-20 h-20 object-cover rounded-lg border border-[#E2E8F0]"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden items-center justify-center w-20 h-20 rounded-lg border-2 border-dashed border-red-300 bg-red-50">
                      <span className="text-red-500 text-xs text-center px-1">Photo not found</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-[#374151] font-medium">Photo selected</p>
                      {attachmentStatusLoading ? (
                        <span className="inline-flex items-center gap-1 text-xs text-[#94A3B8] mt-1" data-testid="attachment-status">
                          <Loader2 className="w-3 h-3 animate-spin" /> Checking attachment…
                        </span>
                      ) : attachmentStatus ? (
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-medium mt-1 px-2 py-0.5 rounded-full ${
                            attachmentStatus.available
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                          data-testid="attachment-status"
                        >
                          {attachmentStatus.available ? (
                            <><CheckCircle className="w-3 h-3" /> Stored — will attach</>
                          ) : (
                            <><AlertTriangle className="w-3 h-3" /> {attachmentStatus.detail || 'Missing — won\'t attach'}</>
                          )}
                        </span>
                      ) : (
                        <p className="text-xs text-[#94A3B8]">If the image doesn't show, please upload a new one</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setEmailForm(prev => ({ ...prev, proofPhotoUrl: '' })); setAttachmentSizeBytes(0); }}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer">
                      <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-4 text-center hover:border-[#0D9488] transition-colors">
                        <Upload className="w-6 h-6 mx-auto text-[#94A3B8] mb-2" />
                        <p className="text-sm text-[#64748B]">
                          {isUploadingProofPhoto ? 'Uploading...' : 'Click to upload proof photo'}
                        </p>
                        <p className="text-xs text-[#94A3B8]">JPG, PNG up to 10MB</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProofPhotoUpload}
                        className="hidden"
                        disabled={isUploadingProofPhoto}
                      />
                    </label>
                    {isUploadingProofPhoto && (
                      <Loader2 className="w-6 h-6 animate-spin text-[#0D9488]" />
                    )}
                  </div>
                )}
                
                {/* Show existing proof photos from booking */}
                {selectedBooking?.proof_files?.length > 0 && !emailForm.proofPhotoUrl && (
                  <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                    <p className="text-xs text-[#64748B] mb-2">Or select from existing photos:</p>
                    <div className="flex gap-2 flex-wrap">
                      {selectedBooking.proof_files.map((photo, idx) => {
                        // Extract URL from photo object or string
                        const photoUrl = typeof photo === 'object' && photo.url ? photo.url : photo;
                        const displayUrl = photoUrl.startsWith('/api/') ? `${API_URL}${photoUrl}` : photoUrl;
                        
                        return (
                          <button
                            key={idx}
                            onClick={async () => {
                              setEmailForm(prev => ({ ...prev, proofPhotoUrl: photoUrl }));
                              // Probe size via HEAD request so the size guard works for existing photos too
                              try {
                                const head = await fetch(displayUrl, { method: 'HEAD' });
                                const len = parseInt(head.headers.get('content-length') || '0', 10);
                                setAttachmentSizeBytes(len);
                              } catch {
                                setAttachmentSizeBytes(0);
                              }
                            }}
                            className="w-16 h-16 rounded-lg overflow-hidden border-2 border-transparent hover:border-[#0D9488] transition-colors"
                          >
                            <img src={displayUrl} alt={`Proof ${idx + 1}`} className="w-full h-full object-cover" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Send Button */}
              <div className="pt-4 border-t border-[#E2E8F0] space-y-3">
                {attachmentSizeBytes > 0 && (
                  <div
                    className={`rounded-md p-3 text-xs flex items-start gap-2 ${
                      attachmentSizeBytes > ATTACHMENT_MAX_BYTES
                        ? 'bg-red-50 border border-red-200 text-red-800'
                        : 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                    }`}
                    data-testid="attachment-size-banner"
                  >
                    <span className="flex-shrink-0 mt-0.5">
                      {attachmentSizeBytes > ATTACHMENT_MAX_BYTES ? '⚠️' : '✓'}
                    </span>
                    <span>
                      <strong>Attachment size: {(attachmentSizeBytes / (1024 * 1024)).toFixed(2)} MB</strong>
                      {attachmentSizeBytes > ATTACHMENT_MAX_BYTES ? (
                        <> — exceeds the 2 MB email limit. Remove the attachment or replace with a smaller photo before sending.</>
                      ) : (
                        <> — within the 2 MB email limit ✓</>
                      )}
                    </span>
                  </div>
                )}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowEmailModal(false)}
                    disabled={isSendingEmail}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    onClick={sendCollectionEmail}
                    disabled={isSendingEmail || !emailForm.to || attachmentSizeBytes > ATTACHMENT_MAX_BYTES}
                    data-testid="send-email-btn"
                  >
                    {isSendingEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Booking Modal */}
      {showAddBookingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-[#111111] flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#0D9488]" />
                Add New Booking
              </h2>
              <button
                onClick={() => {
                  setShowAddBookingModal(false);
                  setAddBookingError('');
                }}
                className="text-[#94A3B8] hover:text-[#111111]"
                data-testid="close-add-booking-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {addBookingError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {addBookingError}
                </div>
              )}
              
              {/* Client Type Selection */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Client Type
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAddBookingForm(prev => ({ ...prev, clientType: 'subscription', clientId: '', clientName: '', clientMobile: '', clientEmail: '', clientBusinessName: '' }))}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                      addBookingForm.clientType === 'subscription'
                        ? 'border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]'
                        : 'border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8]'
                    }`}
                    data-testid="client-type-subscription"
                  >
                    <Package className="w-4 h-4 inline mr-2" />
                    Subscription Client
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddBookingForm(prev => ({ ...prev, clientType: 'one_time', clientId: '' }))}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                      addBookingForm.clientType === 'one_time'
                        ? 'border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]'
                        : 'border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8]'
                    }`}
                    data-testid="client-type-onetime"
                  >
                    <User className="w-4 h-4 inline mr-2" />
                    One-Time Client
                  </button>
                </div>
              </div>
              
              {/* Subscription Client Selection */}
              {addBookingForm.clientType === 'subscription' && (
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">
                    Select Subscription Client <span className="text-red-500">*</span>
                  </label>
                  {isLoadingClients ? (
                    <div className="flex items-center gap-2 text-[#64748B] py-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading clients...
                    </div>
                  ) : (
                    <select
                      value={addBookingForm.clientId}
                      onChange={(e) => handleClientSelect(e.target.value)}
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                      data-testid="subscription-client-select"
                    >
                      <option value="">-- Select a client --</option>
                      {subscriptionClients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.business_name || client.name} ({client.mobile})
                        </option>
                      ))}
                    </select>
                  )}
                  {addBookingForm.clientId && (
                    <div className="mt-2 p-3 bg-[#F0FDFA] border border-[#0D9488]/20 rounded-lg">
                      <p className="text-sm text-[#0D9488] font-medium">{addBookingForm.clientBusinessName || addBookingForm.clientName}</p>
                      <p className="text-xs text-[#64748B]">{addBookingForm.clientMobile} • {addBookingForm.clientEmail || 'No email'}</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* One-Time Client Details */}
              {addBookingForm.clientType === 'one_time' && (
                <div className="space-y-4 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                  <h3 className="text-sm font-semibold text-[#374151]">Client Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-[#64748B] mb-1">Client Name <span className="text-red-500">*</span></label>
                      <Input
                        value={addBookingForm.clientName}
                        onChange={(e) => setAddBookingForm(prev => ({ ...prev, clientName: e.target.value }))}
                        placeholder="John Smith"
                        data-testid="client-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#64748B] mb-1">Client Mobile <span className="text-red-500">*</span></label>
                      <Input
                        value={addBookingForm.clientMobile}
                        onChange={(e) => setAddBookingForm(prev => ({ ...prev, clientMobile: e.target.value }))}
                        placeholder="04XX XXX XXX"
                        data-testid="client-mobile-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#64748B] mb-1">Client Email</label>
                      <Input
                        type="email"
                        value={addBookingForm.clientEmail}
                        onChange={(e) => setAddBookingForm(prev => ({ ...prev, clientEmail: e.target.value }))}
                        placeholder="client@email.com"
                        data-testid="client-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#64748B] mb-1">Business Name</label>
                      <Input
                        value={addBookingForm.clientBusinessName}
                        onChange={(e) => setAddBookingForm(prev => ({ ...prev, clientBusinessName: e.target.value }))}
                        placeholder="Company Pty Ltd"
                        data-testid="client-business-input"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Site/Collection Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-[#374151] flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Collection Site Details
                </h3>
                
                {/* Site Address with Autocomplete */}
                <div className="relative">
                  <label className="block text-sm text-[#64748B] mb-1">Site Address <span className="text-red-500">*</span></label>
                  <Input
                    value={addBookingForm.siteAddress}
                    onChange={(e) => handleAddBookingAddressChange(e.target.value)}
                    onFocus={() => addBookingForm.siteAddress.length >= 3 && setShowAddBookingAddressSuggestions(true)}
                    placeholder="Start typing address..."
                    data-testid="site-address-input"
                  />
                  {showAddBookingAddressSuggestions && addBookingAddressSuggestions.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {addBookingAddressSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectAddBookingAddress(suggestion)}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-[#F8FAFC] border-b border-[#E2E8F0] last:border-0"
                        >
                          <MapPin className="w-4 h-4 inline mr-2 text-[#94A3B8]" />
                          {suggestion.description}
                        </button>
                      ))}
                    </div>
                  )}
                  {isLoadingAddresses && (
                    <div className="absolute right-3 top-8">
                      <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" />
                    </div>
                  )}
                </div>
                
                {/* Customer (Person at Site) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#64748B] mb-1">Customer Name <span className="text-red-500">*</span></label>
                    <Input
                      value={addBookingForm.customerName}
                      onChange={(e) => setAddBookingForm(prev => ({ ...prev, customerName: e.target.value }))}
                      placeholder="Person at site"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#64748B] mb-1">Customer Mobile <span className="text-red-500">*</span></label>
                    <Input
                      value={addBookingForm.customerMobile}
                      onChange={(e) => setAddBookingForm(prev => ({ ...prev, customerMobile: e.target.value }))}
                      placeholder="04XX XXX XXX"
                      data-testid="customer-mobile-input"
                    />
                  </div>
                </div>
                
                {/* Site Notes */}
                <div>
                  <label className="block text-sm text-[#64748B] mb-1">Site Notes</label>
                  <textarea
                    value={addBookingForm.siteNotes}
                    onChange={(e) => setAddBookingForm(prev => ({ ...prev, siteNotes: e.target.value }))}
                    placeholder="Access instructions, specific location on site, etc."
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0D9488] min-h-[80px]"
                    data-testid="site-notes-input"
                  />
                </div>
                
                {/* Pallets Toggle */}
                <div className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg">
                  <span className="text-sm text-[#374151]">Pallets on site?</span>
                  <button
                    type="button"
                    onClick={() => setAddBookingForm(prev => ({ ...prev, hasPallets: !prev.hasPallets }))}
                    className={`w-12 h-6 rounded-full transition-colors ${addBookingForm.hasPallets ? 'bg-[#0D9488]' : 'bg-[#E2E8F0]'}`}
                    data-testid="pallets-toggle"
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${addBookingForm.hasPallets ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                
                {addBookingForm.hasPallets && (
                  <div>
                    <label className="block text-sm text-[#64748B] mb-1">Number of Pallets</label>
                    <Input
                      type="number"
                      min="1"
                      value={addBookingForm.palletCount}
                      onChange={(e) => setAddBookingForm(prev => ({ ...prev, palletCount: e.target.value }))}
                      placeholder="1"
                      className="w-32"
                      data-testid="pallet-count-input"
                    />
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-[#E2E8F0]">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowAddBookingModal(false);
                    setAddBookingError('');
                  }}
                  data-testid="cancel-add-booking"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white"
                  onClick={handleCreateBooking}
                  disabled={isCreatingBooking}
                  data-testid="create-booking-btn"
                >
                  {isCreatingBooking ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Booking
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
